import React from "react";
import { useTranslation } from "react-i18next";
import { BRAND_COLORS, SEMANTIC_COLORS } from "./constants";
import SectionHeading from "@/components/ui/SectionHeading";

const PerPromptScores = ({ queryScores }) => {
  const { t } = useTranslation();
  const perPromptData = queryScores.slice(1).map((item) => {
    const original = Math.round((item.oldScore || 0) * 100);
    const current = Math.round((item.newScore || 0) * 100);
    return {
      prompt: item.query,
      original,
      new: current,
      delta: current - original,
    };
  });

  return (
    <div className="mb-4" data-testid="per-prompt-scores">
      <SectionHeading className="mb-1">
        {t("contentOptimizer.perPromptScores", "Per-Prompt Scores")}
      </SectionHeading>
      <p className="mb-3 rs-subtext">
        {t(
          "contentOptimizer.perPromptScoresSubtitle",
          "Scores for other content affected by the changes made to the draft",
        )}
      </p>
      <div>
        {perPromptData.map((item, index) => {
          const isIncrease = item.new > item.original;
          const isDecrease = item.new < item.original;
          const currentPercent = item.new;
          const originalPercent = item.original;
          const deltaWidth = Math.abs(item.delta);

          return (
            <div
              key={index}
              className="p-4 bg-white card-radius border mb-3"
              data-testid="per-prompt-entry"
            >
              <div className="mb-2">
                <span className="fw-semibold rs-prompt">{item.prompt}</span>
              </div>

              {/* Bar visualization */}
              <div className="position-relative">
                <div
                  className="rounded-pill overflow-hidden position-relative"
                  style={{
                    height: "12px",
                    backgroundColor: SEMANTIC_COLORS.barTrack,
                  }}
                >
                  {isIncrease && (
                    <>
                      {/* Base portion up to original (lighter green) */}
                      <div
                        className="position-absolute top-0 start-0 h-100 rounded-pill"
                        style={{
                          width: `${originalPercent}%`,
                          backgroundColor: SEMANTIC_COLORS.successLight,
                        }}
                      />
                      {/* Delta segment (dark green) */}
                      <div
                        className="position-absolute top-0 h-100 rounded-end-pill"
                        style={{
                          left: `${originalPercent}%`,
                          width: `${deltaWidth}%`,
                          backgroundColor: SEMANTIC_COLORS.successDark,
                        }}
                      />
                    </>
                  )}

                  {isDecrease && (
                    <>
                      {/* Base portion up to current (brand primary) */}
                      <div
                        className="position-absolute top-0 start-0 h-100 rounded-pill"
                        style={{
                          width: `${currentPercent}%`,
                          backgroundColor: BRAND_COLORS.primary,
                        }}
                      />
                      {/* Lost portion (red segment with outline) */}
                      <div
                        className="position-absolute top-0 h-100 rounded-end-pill border"
                        style={{
                          left: `${currentPercent}%`,
                          width: `${deltaWidth}%`,
                          backgroundColor: `${SEMANTIC_COLORS.errorDark}cc`,
                          borderColor: `${SEMANTIC_COLORS.errorDark}99`,
                        }}
                      />
                    </>
                  )}

                  {!isIncrease && !isDecrease && (
                    /* No change - simple bar */
                    <div
                      className="position-absolute top-0 start-0 h-100 rounded-pill"
                      style={{
                        width: `${currentPercent}%`,
                        backgroundColor: BRAND_COLORS.primary,
                      }}
                    />
                  )}
                </div>
              </div>

              {/* Labels */}
              <div className="d-flex align-items-center gap-2 mt-2 rs-subtext">
                <span>
                  <span style={{ color: "var(--text-secondary)" }}>
                    {t("contentOptimizer.original", "Original")}:
                  </span>{" "}
                  <span
                    className="fw-semibold"
                    style={{ color: "var(--text-primary)" }}
                  >
                    {item.original}
                  </span>
                </span>
                <span style={{ color: "var(--text-muted)" }}>·</span>
                <span>
                  <span style={{ color: "var(--text-secondary)" }}>
                    {t("contentOptimizer.current", "Current")}:
                  </span>{" "}
                  <span
                    className="fw-semibold"
                    style={{ color: "var(--text-primary)" }}
                  >
                    {item.new}
                  </span>
                </span>
                <span style={{ color: "var(--text-muted)" }}>·</span>
                <span>
                  <span style={{ color: "var(--text-secondary)" }}>
                    {t("dashboard.change", "Change")}:
                  </span>{" "}
                  <span
                    className="fw-semibold"
                    style={{
                      color:
                        item.delta > 0
                          ? "var(--success-dark)"
                          : item.delta < 0
                            ? "var(--error-dark)"
                            : "var(--text-secondary)",
                    }}
                  >
                    {item.delta > 0 ? "+" : ""}
                    {item.delta}
                  </span>
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PerPromptScores;
